import { FieldPath, FieldValues, RegisterOptions, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextArea, { type TTextAreaProps } from "../text-area/text-area";
import { THookFormFieldProps } from "./hook-form-types";
import { getErrorMessage } from "./hook-form-helper";
import { useEffect } from "react";

export type THookFormTextAreaRules<T extends FieldValues = FieldValues> = Pick<
  RegisterOptions<T, FieldPath<T>>,
  "required" | "maxLength" | "minLength"
>;

type THookFormTextAreaProps<TFieldValues extends FieldValues> = THookFormFieldProps<TFieldValues> & {
  rules?: THookFormTextAreaRules<TFieldValues>;
} & Pick<
    TTextAreaProps,
    "label" | "helperText" | "placeholder" | "isDisabled" | "isReadOnly" | "maxLength" | "minLength"
  >;

export const HookFormTextArea = <T extends FieldValues>({
  rules,
  control,
  name,
  label,
  helperText,
  placeholder,
  isDisabled,
  isReadOnly,
  maxLength,
  minLength,
  defaultValue,
}: THookFormTextAreaProps<T>) => {
  const { t } = useTranslation();

  const { field, fieldState } = useController({
    name: name,
    control: control,
    defaultValue: defaultValue,
    disabled: isDisabled,
    rules: rules,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (defaultValue) {
      field.onChange(defaultValue);
    }
  }, []);

  const { invalid, error } = fieldState;

  return (
    <TextArea
      name={field.name}
      label={label}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      helperText={helperText}
      placeholder={placeholder}
      onChange={field.onChange}
      errorText={getErrorMessage(t, error)}
      isValid={!invalid}
      value={field.value}
      maxLength={maxLength}
      minLength={minLength}
    />
  );
};
